// Convert blueprint to AI Builder Prompt for tools like Emergent, Replit, Cursor, Bolt

// Builder-specific configurations
const BUILDER_CONFIGS = {
    emergent: {
        name: 'Emergent',
        intro: 'Build this app using your full-stack capabilities.',
        techStack: '- Use your default React + FastAPI + MongoDB stack\n- Leverage Tailwind CSS and Shadcn UI components\n- Use localStorage for MVP, MongoDB for persistence',
        closing: 'Please implement this as a complete MVP. Focus on core functionality first, then polish the UI.',
        color: 'primary'
    },
    replit: {
        name: 'Replit',
        intro: 'Create this as a Replit project that can be forked and run instantly.',
        techStack: '- Frontend: React or vanilla JS with Tailwind CSS\n- Backend: Node.js/Express or Python/Flask\n- Database: Replit DB or SQLite\n- Keep dependencies minimal for fast startup',
        closing: 'Make it easy to fork and customize. Include a clear README with setup instructions.',
        color: 'accent'
    },
    cursor: {
        name: 'Cursor',
        intro: 'Help me build this app step by step in my local development environment.',
        techStack: '- Frontend: React with Vite for fast HMR\n- Backend: Node.js/Express or FastAPI\n- Database: PostgreSQL or MongoDB\n- Use TypeScript where possible',
        closing: 'Guide me through each file creation. Explain key decisions as we build.',
        color: 'success'
    },
    bolt: {
        name: 'Bolt',
        intro: 'Generate this complete app in a single output.',
        techStack: '- Use React for frontend\n- Use a simple backend API\n- Include all necessary files in the output\n- Make it deployable immediately',
        closing: 'Generate all files needed for a working prototype. Prioritize speed and simplicity.',
        color: 'warning'
    },
    chatgpt: {
        name: 'ChatGPT',
        intro: 'I need your help building this web application. Please provide detailed code and explanations.',
        techStack: '- Frontend: React with modern hooks\n- Backend: Node.js/Express or Python/FastAPI\n- Database: MongoDB or PostgreSQL\n- Include step-by-step implementation guidance',
        closing: 'Please provide complete, working code for each component. Explain any complex logic.',
        color: 'secondary'
    }
};

export const getBuilderConfigs = () => BUILDER_CONFIGS;

export const generateBuilderPrompt = (blueprint, idea, builder = 'emergent') => {
    if (!blueprint) return '';
    
    const config = BUILDER_CONFIGS[builder] || BUILDER_CONFIGS.emergent;
    const lines = [];
    
    // Builder-specific intro
    lines.push(`Build a mobile-responsive web app called "${blueprint.app_name}".`);
    lines.push('');
    lines.push(config.intro);
    lines.push('');
    
    // Original idea context
    lines.push(`## Original Idea`);
    lines.push(idea);
    lines.push('');
    
    // Summary
    lines.push(`## App Summary`);
    lines.push(blueprint.summary);
    lines.push('');

    // Core features
    lines.push(`## Core Features`);
    lines.push('Implement the following features:');
    blueprint.features.forEach((feature, i) => {
        const priority = feature.priority === 'core' ? '(REQUIRED)' : 
                        feature.priority === 'nice-to-have' ? '(if time permits)' : '(future phase)';
        lines.push(`${i + 1}. **${feature.name}** ${priority}: ${feature.description}`);
    });
    lines.push('');

    // Screens/Pages
    lines.push(`## Screens / Pages`);
    lines.push('Create these screens/pages:');
    blueprint.screens.forEach((screen, i) => {
        lines.push(`${i + 1}. **${screen.name}**: ${screen.purpose}`);
        lines.push(`   Components needed: ${screen.components.join(', ')}`);
    });
    lines.push('');

    // Database structure
    lines.push(`## Database Structure`);
    lines.push('Use this database schema:');
    blueprint.database.forEach(table => {
        lines.push(`- **${table.name}**: ${table.fields.join(', ')}`);
        if (table.relationships) {
            lines.push(`  Relationships: ${table.relationships}`);
        }
    });
    lines.push('');

    // API endpoints
    lines.push(`## API Endpoints`);
    lines.push('Implement these API endpoints:');
    blueprint.api_endpoints.forEach(endpoint => {
        lines.push(`- ${endpoint.method} ${endpoint.path} — ${endpoint.description}`);
    });
    lines.push('');

    // Build order
    lines.push(`## Build Order`);
    lines.push('Follow this implementation order:');
    blueprint.build_order.forEach(step => {
        lines.push(`**Phase ${step.order}: ${step.phase}**`);
        step.tasks.forEach(task => {
            lines.push(`  - ${task}`);
        });
    });
    lines.push('');

    // UX guidance
    lines.push(`## UX Guidelines`);
    lines.push('- Keep the UI clean, modern, and beginner-friendly');
    lines.push('- Use a dark theme with good contrast');
    lines.push('- Make all interactive elements clearly visible');
    lines.push('- Ensure mobile responsiveness from the start');
    lines.push('- Add loading states for async operations');
    lines.push('- Include helpful error messages');
    lines.push('');

    // Builder-specific tech stack
    lines.push(`## Tech Stack`);
    lines.push(config.techStack);
    lines.push('');

    // Builder-specific closing
    lines.push(`## Final Notes`);
    lines.push(config.closing);

    return lines.join('\n');
};

export const generateAIBuilderPrompt = (blueprint, idea) => {
    return generateBuilderPrompt(blueprint, idea, 'emergent');
};

// Generate a shorter, more concise prompt for quick copy
export const generateQuickPrompt = (blueprint, idea) => {
    if (!blueprint) return '';

    const coreFeatures = blueprint.features
        .filter(f => f.priority === 'core')
        .map(f => f.name)
        .join(', ');

    const screens = blueprint.screens.map(s => s.name).join(', ');
    
    const tables = blueprint.database.map(t => t.name).join(', ');

    return `Build a mobile-responsive web app called "${blueprint.app_name}".

${blueprint.summary}

Core Features: ${coreFeatures}

Screens: ${screens}

Database Tables: ${tables}

API Endpoints: ${blueprint.api_endpoints.length} endpoints for CRUD operations

Build this with React frontend, a REST API backend, and MongoDB. Keep the UI clean, modern, and beginner-friendly. Include proper error handling and loading states.`;
};
